import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  FlatList,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  RefreshControl,
  Image,
  Keyboard,
  Alert,
  Linking,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { ClubsStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../../components/common/ThemedText';
import LinkText from '../../components/common/LinkText';
import ChatMessageSkeleton from '../../components/common/ChatMessageSkeleton';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { getClubById, Club } from '../../lib/api/clubs';
import { apiBaseUrl } from '../../config/env';

type ClubChatRouteProp = RouteProp<ClubsStackParamList, 'ClubChat'>;

const ClubChatScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<ClubChatRouteProp>();
  const { theme, isDark } = useTheme();
  const { clubId, conversationId: routeConversationId } = route.params;
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const {
    currentMessages,
    fetchMessages,
    sendMessage,
    refreshMessages,
    markAsRead,
    clearMessages,
    getClubConversation,
  } = useChat();

  const fetchMessagesRef = useRef(fetchMessages);
  const markAsReadRef = useRef(markAsRead);
  const getClubConversationRef = useRef(getClubConversation);

  useEffect(() => {
    fetchMessagesRef.current = fetchMessages;
  }, [fetchMessages]);

  useEffect(() => {
    markAsReadRef.current = markAsRead;
  }, [markAsRead]);

  useEffect(() => {
    getClubConversationRef.current = getClubConversation;
  }, [getClubConversation]);

  const [club, setClub] = useState<Club | null>(null);
  const [conversationId, setConversationId] = useState<string | undefined>(routeConversationId);
  const [loadingClub, setLoadingClub] = useState(true);
  const [loadingChat, setLoadingChat] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ uri: string; width: number; height: number } | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  useEffect(() => {
    if (Platform.OS === 'android') {
      const showSubscription = Keyboard.addListener('keyboardDidShow', (e) => {
        setKeyboardHeight(e.endCoordinates.height);
      });
      const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
        setKeyboardHeight(0);
      });

      return () => {
        showSubscription.remove();
        hideSubscription.remove();
      };
    }
  }, []);

  useEffect(() => {
    clearMessages();
    return () => {
      clearMessages();
    };
  }, [clubId]);

  useEffect(() => {
    setConversationId(routeConversationId);
  }, [routeConversationId, clubId]);

  useEffect(() => {
    let isMounted = true;

    const loadClubDetails = async () => {
      try {
        setLoadingClub(true);
        const clubData = await getClubById(clubId);
        if (isMounted) {
          setClub(clubData);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading club:', err);
        if (isMounted) {
          setError('Unable to load club details');
        }
      } finally {
        if (isMounted) {
          setLoadingClub(false);
        }
      }
    };

    loadClubDetails();

    return () => {
      isMounted = false;
    };
  }, [clubId]);

  useEffect(() => {
    if (!club || club.membershipStatus !== 'joined') {
      setLoadingChat(false);
      return;
    }

    let isMounted = true;

    const loadConversation = async () => {
      try {
        setLoadingChat(true);
        let resolvedConversationId = conversationId;

        if (!resolvedConversationId) {
          const conversation = await getClubConversationRef.current(clubId);
          if (!isMounted) return;
          resolvedConversationId = conversation.id;
          setConversationId(conversation.id);
        }

        if (!resolvedConversationId || !isMounted) {
          return;
        }

        await fetchMessagesRef.current(resolvedConversationId);
        if (!isMounted) return;
        await markAsReadRef.current(resolvedConversationId);
        if (isMounted) {
          setError(null);
        }
      } catch (err) {
        console.error('Error initializing club chat:', err);
        if (isMounted) {
          setError('Unable to load chat messages');
        }
      } finally {
        if (isMounted) {
          setLoadingChat(false);
        }
      }
    };

    loadConversation();

    return () => {
      isMounted = false;
    };
  }, [club, clubId, conversationId]);

  const handleBack = () => {
    navigation.goBack();
  };

  const handleSendMessage = async () => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage || isSending) {
      return;
    }

    setIsSending(true);
    const tempMessageText = trimmedMessage;
    setMessageText(''); // Clear input immediately for better UX

    try {
      let targetConversationId = conversationId;

      if (!targetConversationId) {
        const conversation = await getClubConversationRef.current(clubId);
        targetConversationId = conversation.id;
        setConversationId(conversation.id);
      }

      if (!targetConversationId) {
        setMessageText(tempMessageText); // Restore on error
        return;
      }

      await sendMessage(targetConversationId, tempMessageText);
    } catch (err) {
      console.error('Error sending club message:', err);
      setMessageText(tempMessageText); // Restore on error
      Alert.alert('Error', 'Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleAttachDocument = async () => {
    try {
      // Pick a document - Only PDFs allowed
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];

      // Validate it's actually a PDF
      if (file.mimeType && file.mimeType !== 'application/pdf') {
        Alert.alert('Invalid File Type', 'Only PDF files are allowed. Please select a PDF file.');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size && file.size > 10 * 1024 * 1024) {
        Alert.alert('File Too Large', 'Please select a file smaller than 10MB');
        return;
      }

      setUploading(true);

      // Upload file to backend
      const token = await AsyncStorage.getItem('@lnb_token');
      const formData = new FormData();
      
      // Handle web differently
      if (Platform.OS === 'web') {
        const response = await fetch(file.uri);
        const blob = await response.blob();
        const webFile = new File([blob], file.name, { type: file.mimeType || 'application/octet-stream' });
        formData.append('file', webFile);
      } else {
        formData.append('file', {
          uri: file.uri,
          type: file.mimeType || 'application/octet-stream',
          name: file.name,
        } as any);
      }

      const uploadResponse = await fetch(`${apiBaseUrl}/api/upload/pdf`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to upload file');
      }

      const uploadData = await uploadResponse.json();

      // Get or create conversation
      let targetConversationId = conversationId;
      if (!targetConversationId) {
        const conversation = await getClubConversationRef.current(clubId);
        targetConversationId = conversation.id;
        setConversationId(conversation.id);
      }

      if (!targetConversationId) {
        throw new Error('No conversation available');
      }

      // Send message with file attachment
      await sendMessage(
        targetConversationId,
        file.name,
        'pdf',
        uploadData.url,
        file.name,
        file.size
      );
    } catch (error: any) {
      console.error('Error attaching document:', error);
      const errorMessage = error.message || 'Failed to attach document. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleAttachImage = async () => {
    try {
      // Request permission
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Required', 'Please grant access to your photos to share images.');
        return;
      }

      // Pick an image
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
      });

      if (result.canceled) {
        return;
      }

      const image = result.assets[0];
      setSelectedImage({
        uri: image.uri,
        width: image.width,
        height: image.height,
      });
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const handleSendImage = async () => {
    if (!selectedImage) return;

    try {
      setUploading(true);
      console.log('📤 [ClubChat] Starting image upload...');
      console.log('📤 Image URI:', selectedImage.uri);
      console.log('📤 Platform:', Platform.OS);

      // Upload image to backend
      const token = await AsyncStorage.getItem('@lnb_token');
      const formData = new FormData();
      
      // Get file extension from URI
      const uriParts = selectedImage.uri.split('.');
      const fileType = uriParts[uriParts.length - 1];
      const filename = `chat-image-${Date.now()}.${fileType}`;
      const mimeType = `image/${fileType}`;
      
      console.log('📤 File info:', { filename, mimeType });
      
      // Handle web differently
      if (Platform.OS === 'web') {
        console.log('🌐 Web platform - converting blob to File...');
        const response = await fetch(selectedImage.uri);
        const blob = await response.blob();
        console.log('📦 Blob created:', { size: blob.size, type: blob.type });
        
        // Use blob's actual MIME type if available
        const actualMimeType = blob.type || mimeType;
        const file = new File([blob], filename, { type: actualMimeType });
        console.log('📄 File created:', { name: file.name, size: file.size, type: file.type });
        formData.append('image', file);
      } else {
        formData.append('image', {
          uri: selectedImage.uri,
          type: mimeType,
          name: filename,
        } as any);
      }

      console.log('🚀 Uploading to:', `${apiBaseUrl}/api/upload/chat-image`);
      const uploadResponse = await fetch(`${apiBaseUrl}/api/upload/chat-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      console.log('📥 Upload response status:', uploadResponse.status);

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('❌ Upload failed:', errorText);
        throw new Error('Failed to upload image');
      }

      const uploadData = await uploadResponse.json();
      console.log('✅ Upload successful:', uploadData);

      // Get or create conversation
      let targetConversationId = conversationId;
      if (!targetConversationId) {
        const conversation = await getClubConversationRef.current(clubId);
        targetConversationId = conversation.id;
        setConversationId(conversation.id);
      }

      if (!targetConversationId) {
        throw new Error('No conversation available');
      }

      // Send message with image attachment
      await sendMessage(
        targetConversationId,
        'Image',
        'image',
        uploadData.url,
        uploadData.filename,
        uploadData.size
      );

      // Clear selected image after sending
      setSelectedImage(null);
    } catch (error) {
      console.error('Error sending image:', error);
      Alert.alert('Error', 'Failed to send image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRefresh = async () => {
    if (!conversationId) {
      return;
    }

    try {
      setRefreshing(true);
      await refreshMessages(conversationId);
      await markAsRead(conversationId);
    } catch (err) {
      console.error('Error refreshing club messages:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const renderMessage = ({ item }: { item: typeof currentMessages[number] }) => {
    const isOwnMessage = item.sender.id === user?.userId;

    return (
      <View style={[styles.messageContainer, isOwnMessage && styles.messageContainerOwn]}>
        {!isOwnMessage && (
          <View style={[styles.messageAvatar, { backgroundColor: theme.primary }]}>
            {item.sender.photo ? (
              <Image source={{ uri: item.sender.photo }} style={styles.avatarImage} />
            ) : (
              <Ionicons name="person" size={16} color="#000000" />
            )}
          </View>
        )}
        <View style={[styles.messageBubble, { backgroundColor: isOwnMessage ? (isDark ? '#4A3A1F' : 'rgba(196, 150, 62, 0.15)') : theme.card }, item.type === 'image' && styles.imageBubble]}>
          {!isOwnMessage && (
            <ThemedText style={[styles.messageSender, { color: theme.textSecondary }]}>
              {item.sender.name}
            </ThemedText>
          )}
          {item.type === 'image' && item.fileUrl ? (
            <TouchableOpacity onPress={() => setFullscreenImage(item.fileUrl!)} activeOpacity={0.9}>
              <Image 
                source={{ uri: item.fileUrl }} 
                style={styles.messageImage}
                resizeMode="cover"
              />
              <ThemedText style={[
                styles.messageTime,
                styles.imageMessageTime,
                { color: 'rgba(255,255,255,0.9)' }
              ]}>
                {getRelativeTime(item.createdAt)}
              </ThemedText>
            </TouchableOpacity>
          ) : item.type === 'pdf' && item.fileUrl ? (
            <>
              <TouchableOpacity 
                onPress={() => Linking.openURL(item.fileUrl!)}
                style={[
                  styles.pdfAttachment,
                  { backgroundColor: isOwnMessage ? 'rgba(0,0,0,0.1)' : theme.backgroundSecondary }
                ]}
                activeOpacity={0.7}
              >
                <View style={[
                  styles.pdfIconContainer,
                  { backgroundColor: isOwnMessage ? 'rgba(0,0,0,0.15)' : theme.primary + '20' }
                ]}>
                  <Ionicons 
                    name="document-text" 
                    size={28} 
                    color={isOwnMessage ? (isDark ? '#FFFFFF' : '#000000') : theme.primary} 
                  />
                </View>
                <View style={styles.pdfInfo}>
                  <ThemedText style={[
                    styles.pdfFileName,
                    { color: isOwnMessage ? (isDark ? '#FFFFFF' : '#000000') : theme.text }
                  ]} numberOfLines={2}>
                    {item.fileName || item.content || 'Document.pdf'}
                  </ThemedText>
                  <View style={styles.pdfMeta}>
                    {item.fileSize && (
                      <ThemedText style={[
                        styles.pdfFileSize,
                        { color: isOwnMessage ? (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)') : theme.textSecondary }
                      ]}>
                        {(item.fileSize / 1024).toFixed(1)} KB
                      </ThemedText>
                    )}
                    <ThemedText style={[
                      styles.pdfTapHint,
                      { color: isOwnMessage ? (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)') : theme.textTertiary }
                    ]}>
                      • Tap to open
                    </ThemedText>
                  </View>
                </View>
                <Ionicons 
                  name="chevron-forward" 
                  size={20} 
                  color={isOwnMessage ? (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)') : theme.textTertiary} 
                />
              </TouchableOpacity>
              <ThemedText style={[styles.messageTime, { color: isOwnMessage ? (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)') : theme.textTertiary }]}>
                {getRelativeTime(item.createdAt)}
              </ThemedText>
            </>
          ) : (
            <>
              <LinkText
                style={[styles.messageText, { color: isOwnMessage ? (isDark ? '#FFFFFF' : '#000000') : theme.text }]}
                linkColor={isOwnMessage ? (isDark ? '#93C5FD' : '#1D4ED8') : '#4A9EFF'}
              >
                {item.content}
              </LinkText>
              <ThemedText style={[styles.messageTime, { color: isOwnMessage ? (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)') : theme.textTertiary }]}>
                {getRelativeTime(item.createdAt)}
              </ThemedText>
            </>
          )}
        </View>
        {isOwnMessage && (
          <View style={[styles.messageAvatar, { backgroundColor: theme.primary }]}>
            {user?.photo ? (
              <Image source={{ uri: user.photo }} style={styles.avatarImage} />
            ) : (
              <Ionicons name="person" size={16} color="#000000" />
            )}
          </View>
        )}
      </View>
    );
  };

  const renderEmptyState = () => (
    <View style={[styles.emptyState, Platform.OS !== 'web' && { transform: [{ scaleY: -1 }] }]}>
      <Ionicons name="chatbubbles-outline" size={48} color={theme.textTertiary} />
      <ThemedText style={[styles.emptyStateText, { color: theme.textSecondary }]}>No messages yet</ThemedText>
      <ThemedText style={[styles.emptyStateSubtext, { color: theme.textTertiary }]}>Be the first to start the conversation</ThemedText>
    </View>
  );

  const renderJoinBanner = () => (
    <View style={styles.joinBanner}>
      <Ionicons name="lock-closed" size={24} color={theme.textSecondary} />
      <ThemedText style={[styles.joinBannerText, { color: theme.textSecondary }]}>Join the club to participate in chat</ThemedText>
    </View>
  );

  const isMember = club?.membershipStatus === 'joined';
  const chatDisabled = club?.chatEnabled === false;
  const showMessages = isMember && !loadingChat && !loadingClub;

  const Wrapper = Platform.OS === 'ios' ? KeyboardAvoidingView : View;
  const wrapperProps = Platform.OS === 'ios' 
    ? { behavior: 'padding' as const, keyboardVerticalOffset: 0 }
    : {};

  return (
    <Wrapper
      style={[
        { flex: 1, backgroundColor: theme.background, paddingBottom: Platform.OS === 'android' ? keyboardHeight : 0 },
        Platform.OS === 'web' ? styles.webWrapper : undefined,
      ]}
      {...wrapperProps}
    >
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }, Platform.OS === 'web' ? styles.webContainer : undefined]} edges={['top', 'left', 'right']}>
        <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }, Platform.OS === 'web' ? styles.webHeader : undefined]}>
          <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
            <Ionicons name="arrow-back" size={28} color={theme.text} />
          </TouchableOpacity>
          <View style={styles.headerInfo}>
            <ThemedText style={[styles.headerTitle, { color: theme.text }]}>{club?.name || 'Club Chat'}</ThemedText>
            {chatDisabled && (
              <ThemedText style={[styles.headerSubtitle, { color: theme.textSecondary }]}>Chat is disabled</ThemedText>
            )}
          </View>
          <View style={styles.headerButton} />
        </View>
        {error && (
          <View style={styles.errorContainer}>
            <ThemedText style={[styles.errorText, { color: theme.text }]}>{error}</ThemedText>
          </View>
        )}

        <View style={[styles.messagesViewport, Platform.OS === 'web' ? styles.webMessagesViewport : undefined]}>
          {!isMember && !loadingClub ? (
            renderJoinBanner()
          ) : showMessages ? (
            <FlatList
              data={Platform.OS === 'web' ? [...currentMessages].reverse() : currentMessages}
              keyExtractor={(item) => item.id}
              renderItem={renderMessage}
              contentContainerStyle={[
                styles.messagesList,
                currentMessages.length === 0 && styles.messagesListEmpty,
              ]}
              style={[styles.messagesListView, Platform.OS === 'web' ? styles.webList : undefined]}
              showsVerticalScrollIndicator={false}
              inverted={Platform.OS !== 'web'}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={handleRefresh}
                  tintColor={theme.textSecondary}
                />
              }
              ListEmptyComponent={renderEmptyState}
            />
          ) : (
            <View style={styles.loadingContainer}>
              <ChatMessageSkeleton />
              <ChatMessageSkeleton />
              <ChatMessageSkeleton />
              <ChatMessageSkeleton />
            </View>
          )}
        </View>

        {isMember && (
          <View style={[styles.inputContainer, { backgroundColor: theme.backgroundSecondary, borderTopColor: theme.border, paddingBottom: 12 + insets.bottom }, Platform.OS === 'web' ? styles.webInputContainer : undefined]}>
            {chatDisabled ? (
              <View style={[styles.disabledOverlay, { backgroundColor: theme.backgroundSecondary }]}>
                <ThemedText style={[styles.disabledText, { color: theme.textSecondary }]}>Chat is currently disabled by the club admin</ThemedText>
              </View>
            ) : selectedImage ? (
              <View style={styles.imagePreviewContainer}>
                <View style={styles.imagePreviewWrapper}>
                  <Image source={{ uri: selectedImage.uri }} style={styles.imagePreview} resizeMode="cover" />
                  <TouchableOpacity
                    style={[styles.removeImageButton, { backgroundColor: theme.background }]}
                    onPress={handleRemoveImage}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="close" size={20} color={theme.text} />
                  </TouchableOpacity>
                </View>
                <TouchableOpacity
                  style={[styles.sendImageButton, { backgroundColor: theme.primary }]}
                  onPress={handleSendImage}
                  disabled={uploading}
                  activeOpacity={0.7}
                >
                  {uploading ? (
                    <ActivityIndicator size="small" color="#000000" />
                  ) : (
                    <>
                      <Ionicons name="send" size={20} color="#000000" />
                      <ThemedText style={styles.sendImageText}>Send Image</ThemedText>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            ) : (
              <>
                <TouchableOpacity
                  style={[styles.attachButton, { backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }]}
                  onPress={() => setShowAttachmentMenu(true)}
                  disabled={uploading}
                  activeOpacity={0.7}
                >
                  {uploading ? (
                    <ActivityIndicator size="small" color={theme.primary} />
                  ) : (
                    <Ionicons name="add" size={24} color={theme.text} />
                  )}
                </TouchableOpacity>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.card, color: theme.text, borderWidth: 1, borderColor: theme.border }]}
                  placeholder="Mail"
                  value={messageText}
                  onChangeText={setMessageText}
                  multiline
                  maxLength={500}
                  placeholderTextColor={theme.textSecondary}
                />
                <TouchableOpacity
                  style={[styles.sendButton, { backgroundColor: messageText.trim() && !isSending ? theme.primary : theme.disabled }]}
                  onPress={handleSendMessage}
                  disabled={!messageText.trim() || isSending}
                  activeOpacity={0.7}
                >
                  <Ionicons name="send" size={20} color="#000000" />
                </TouchableOpacity>
              </>
            )}
          </View>
        )}
      </SafeAreaView>

      {/* Fullscreen Image Viewer */}
      <Modal
        visible={!!fullscreenImage}
        transparent
        animationType="fade"
        onRequestClose={() => setFullscreenImage(null)}
      >
        <View style={styles.fullscreenImageContainer}>
          <TouchableOpacity 
            style={styles.fullscreenImageOverlay}
            activeOpacity={1}
            onPress={() => setFullscreenImage(null)}
          >
            <TouchableOpacity 
              style={styles.closeFullscreenButton}
              onPress={() => setFullscreenImage(null)}
              activeOpacity={0.7}
            >
              <Ionicons name="close" size={30} color="#FFFFFF" />
            </TouchableOpacity>
            {fullscreenImage && (
              <Image 
                source={{ uri: fullscreenImage }} 
                style={styles.fullscreenImage}
                resizeMode="contain"
              />
            )}
          </TouchableOpacity>
        </View>
      </Modal>

      {/* Attachment Menu */}
      <Modal
        visible={showAttachmentMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAttachmentMenu(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowAttachmentMenu(false)}
        >
          <View style={[styles.attachmentMenu, { backgroundColor: theme.card }]}>
            <TouchableOpacity
              style={styles.attachmentMenuItem}
              onPress={() => {
                setShowAttachmentMenu(false);
                handleAttachImage();
              }}
            >
              <View style={[styles.attachmentMenuIconContainer, { backgroundColor: theme.primary + '20' }]}>
                <Ionicons name="image" size={24} color={theme.primary} />
              </View>
              <ThemedText style={[styles.attachmentMenuText, { color: theme.text }]}>Photo</ThemedText>
            </TouchableOpacity>
            
            <View style={[styles.menuDivider, { backgroundColor: theme.border }]} />
            
            <TouchableOpacity
              style={styles.attachmentMenuItem}
              onPress={() => {
                setShowAttachmentMenu(false);
                handleAttachDocument();
              }}
            >
              <View style={[styles.attachmentMenuIconContainer, { backgroundColor: theme.textSecondary + '20' }]}>
                <Ionicons name="document-attach" size={24} color={theme.textSecondary} />
              </View>
              <ThemedText style={[styles.attachmentMenuText, { color: theme.text }]}>Document</ThemedText>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </Wrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webWrapper: {
    height: '100%',
    minHeight: 0,
  },
  webContainer: {
    height: '100%',
    minHeight: 0,
  },
  messagesViewport: {
    flex: 1,
    minHeight: 0,
  },
  webMessagesViewport: {
    overflow: 'hidden',
  },
  messagesListView: {
    flex: 1,
  },
  webHeader: {
    flexShrink: 0,
  },
  webList: {
    flex: 1,
    minHeight: 0,
  },
  webInputContainer: {
    flexShrink: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#DC3545',
    marginTop: 2,
  },
  messagesList: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  messagesListEmpty: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  loadingContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  messageContainer: {
    flexDirection: 'row',
    marginBottom: 12,
    alignItems: 'flex-end',
  },
  messageContainerOwn: {
    flexDirection: 'row-reverse',
  },
  messageAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  messageBubble: {
    borderRadius: 16,
    padding: 12,
    marginHorizontal: 8,
    maxWidth: '70%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  messageBubbleOwn: {
    maxWidth: '85%',
  },
  messageSender: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 20,
  },
  messageTextOwn: {},
  messageTime: {
    fontSize: 11,
    marginTop: 4,
  },
  messageTimeOwn: {},
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  attachButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  input: {
    flex: 1,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontSize: 15,
    maxHeight: 100,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  sendButtonDisabled: {},
  pdfAttachment: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    marginBottom: 4,
    minWidth: 240,
    maxWidth: '100%',
  },
  pdfIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  pdfInfo: {
    flex: 1,
    marginRight: 8,
  },
  pdfFileName: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    marginBottom: 4,
  },
  pdfMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  pdfFileSize: {
    fontSize: 13,
    fontWeight: '500',
  },
  pdfTapHint: {
    fontSize: 12,
  },
  disabledOverlay: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  disabledText: {
    fontSize: 14,
    textAlign: 'center',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    marginTop: 12,
  },
  emptyStateSubtext: {
    fontSize: 14,
    marginTop: 4,
  },
  joinBanner: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  joinBannerText: {
    fontSize: 16,
    marginTop: 12,
    textAlign: 'center',
  },
  errorContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  errorText: {
    fontSize: 13,
  },
  avatarImage: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  fullscreenImageContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
  },
  fullscreenImageOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullscreenImage: {
    width: '100%',
    height: '100%',
  },
  closeFullscreenButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  attachmentMenu: {
    position: 'absolute',
    bottom: 100,
    left: 20,
    right: 20,
    borderRadius: 16,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  attachmentMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    gap: 16,
  },
  attachmentMenuIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  attachmentMenuText: {
    fontSize: 17,
    fontWeight: '600',
  },
  menuDivider: {
    height: 1,
    marginHorizontal: 8,
  },
  imagePreviewContainer: {
    width: '100%',
    gap: 12,
  },
  imagePreviewWrapper: {
    position: 'relative',
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
  },
  imagePreview: {
    width: '100%',
    height: 200,
    borderRadius: 12,
  },
  removeImageButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  sendImageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    gap: 8,
  },
  sendImageText: {
    color: '#000000',
    fontSize: 16,
    fontWeight: '600',
  },
  imageBubble: {
    padding: 4,
  },
  messageImage: {
    width: 240,
    height: 180,
    borderRadius: 12,
  },
  imageMessageTime: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
});

export default ClubChatScreen;
